import { BsMetaUtil, getLoopBorrowCapDetails, isLeverageLoop } from "@bridgesplit/abf-react";
import { StatProps, StatRow, StatColumn, Text, MEDIA, Row, Column, Tooltip, Icon } from "@bridgesplit/ui";
import { COPY } from "app/constants";
import { formatMultiplier, formatPercent, formatUsd } from "@bridgesplit/utils";
import { useMediaQuery } from "@mui/material";

import { useLoopContext } from "../LoopContext";

export function LoopVaultStats() {
    const { loopExpanded } = useLoopContext();
    const displayAsColumn = useMediaQuery(MEDIA.MD.below);
    const borrowCapWarning = getLoopBorrowCapDetails(loopExpanded);

    const availableLiquidity = Math.min(
        loopExpanded?.loopVault?.principalAmountAvailable ?? 0,
        borrowCapWarning?.remainingGlobalCapacity ?? 0
    );

    const depositedCollateral = BsMetaUtil.formatAmount(
        loopExpanded?.inputToken,
        loopExpanded?.loopVault.collateralDeposited,
        {
            hideSymbol: false
        }
    );

    const stats: StatProps[] = [
        {
            value: (
                <StackedStats
                    caption={formatPercent(loopExpanded?.loopVault.maxLeveragedApyPct)}
                    captionDetail={`Avg: ${formatPercent(loopExpanded?.loopVault.wAvgApy)}`}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: "Max net APY",
            hide: isLeverageLoop(loopExpanded)
        },
        {
            value: (
                <StackedStats
                    caption={formatMultiplier(loopExpanded?.loopVault.maxLeverage)}
                    captionDetail={`Avg: ${formatMultiplier(loopExpanded?.loopVault.wAvgLeverage)}`}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: COPY.MAX_LEVERAGE_TERM
        },
        {
            value: (
                <Tooltip title={borrowCapWarning?.warningMessage ?? undefined}>
                    <StackedStats
                        caption={BsMetaUtil.formatAmount(loopExpanded?.principalToken, availableLiquidity)}
                        captionDetail={`${formatUsd(
                            availableLiquidity * (loopExpanded?.principalOracle.usdPrice ?? 0)
                        )}`}
                        displayAsColumn={displayAsColumn}
                        borrowCapWarning={!!borrowCapWarning?.warningMessage}
                    />
                </Tooltip>
            ),
            caption: COPY.AVAILABLE_LIQUIDITY_TERM
        },
        {
            value: (
                <StackedStats
                    caption={depositedCollateral}
                    captionDetail={formatUsd(loopExpanded?.loopVault.collateralDepositedUsd)}
                    displayAsColumn={displayAsColumn}
                />
            ),
            caption: COPY.DEPOSITED_TERM,
            tooltip: `The total amount of ${BsMetaUtil.getSymbol(
                loopExpanded?.collateralToken
            )} used as collateral across Loops`
        }
    ];

    if (displayAsColumn) {
        return <StatColumn loading={!loopExpanded} stats={stats} />;
    }

    return <StatRow loading={!loopExpanded} stats={stats} />;
}

function StackedStats({
    caption,
    captionDetail,
    displayAsColumn,
    borrowCapWarning
}: {
    caption: string;
    captionDetail: string;
    displayAsColumn: boolean;
    borrowCapWarning?: boolean;
}) {
    const content = (
        <>
            <Text variant="h4" svgColor={borrowCapWarning ? "warning" : undefined}>
                {caption}
                {!!borrowCapWarning && <Icon type="warning" />}
            </Text>

            <Text color="caption" variant="body2">
                {captionDetail}
            </Text>
        </>
    );
    if (borrowCapWarning) {
        return content;
    }
    if (displayAsColumn) {
        return <Row spacing={1}>{content}</Row>;
    }

    return <Column spacing={0}>{content}</Column>;
}
